import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Checkbox } from '@mui/material';
import { Button, Loader, SelectTypeAutocomplete, TextToolTip } from 'components';
import { base64ToFile, Colors, FindErrorItem, FindLoad, Images, renderClientName } from 'utils';
import { signatureRequestActions } from 'store';
import { ApptTable } from './core/apptTable';
import { SignatureCard } from './core/signatureCard';
import { SignatureRequestError } from './core/signatureRequestError';
import { SignatureSubmitted } from './signatureSubmitted';
import {
    getInfoBarCells,
    SIGNATURE_FILE_NAME,
    SIGNATURE_INK_COLOR,
    SIGNATURE_TAB,
    TYPED_SIGNATURE_FONT,
} from './core/constants';

const GET_ACTION_TYPE = 'GET_SIGNATURE_REQUEST';
const SIGN_ACTION_TYPE = 'SIGN_SIGNATURE_REQUEST';

export const SignatureRequest = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { search } = useLocation();
    const request = useSelector((state) => state.signatureRequest.signatureRequest);
    const signed = useSelector((state) => state.signatureRequest.signed);
    const loader = FindLoad(GET_ACTION_TYPE);
    const signLoader = FindLoad(SIGN_ACTION_TYPE);
    const backError = FindErrorItem(GET_ACTION_TYPE);
    const canvasRef = useRef(null);
    const [activeTab, setActiveTab] = useState(SIGNATURE_TAB.DRAW);
    const [hasDrawing, setHasDrawing] = useState(false);
    const [typedName, setTypedName] = useState('');
    const [selectedGuardian, setSelectedGuardian] = useState('');
    const [confirmed, setConfirmed] = useState(false);

    const token = new URLSearchParams(search).get('token');
    const hasSignature = activeTab === SIGNATURE_TAB.DRAW ? hasDrawing : !!typedName.trim();
    const canSubmit = hasSignature && !!selectedGuardian && confirmed && !signLoader?.length;

    useEffect(() => {
        dispatch(signatureRequestActions.getSignatureRequest(token));
    }, [token]);

    const navigateHome = () => {
        navigate('/');
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const handleCanvasRef = (ref) => {
        canvasRef.current = ref;
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setHasDrawing(false);
    };

    const generateTypedSignature = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 480;
        canvas.height = 120;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.font = TYPED_SIGNATURE_FONT;
        ctx.fillStyle = SIGNATURE_INK_COLOR;
        ctx.textBaseline = 'middle';
        ctx.fillText(typedName, 20, 60);
        return canvas.toDataURL('image/png');
    };

    const handleSubmit = () => {
        if (!canSubmit) return;

        const signatureData =
            activeTab === SIGNATURE_TAB.DRAW
                ? canvasRef.current?.current?.getTrimmedCanvas().toDataURL('image/png')
                : generateTypedSignature();
        if (!signatureData) return;

        const file = base64ToFile(signatureData, SIGNATURE_FILE_NAME);
        dispatch(signatureRequestActions.signSignatureRequest(token, file, selectedGuardian, confirmed));
    };

    const renderBody = () => {
        if (loader?.length) return <Loader />;
        if (backError?.error) return <SignatureRequestError error={backError.error} />;
        if (!request) return null;

        if (signed) return <SignatureSubmitted request={request} />;

        return (
            <div className="signature-request-body">
                <div className="signature-request-box">
                    <p className="signature-request-eyebrow">Signature Request</p>
                    <p className="signature-request-title">Review &amp; Sign</p>
                    <p className="signature-request-subtitle">
                        Please review the appointment information below and provide your Client/Guardian signature.
                    </p>

                    <div className="signature-request-info-bar">
                        {getInfoBarCells(request).map(({ label, value }) => (
                            <div key={label} className="signature-request-info-cell">
                                <p className='signature-request-info-label'>
                                    {label}
                                </p>
                                <TextToolTip
                                    classNameWrapper={'signature-request-info-value-wrap'}
                                    classNameText={'signature-request-info-value'}
                                    text={value}
                                    content={value}
                                />
                            </div>
                        ))}
                    </div>

                    <ApptTable appointments={request.appointments} />

                    <SignatureCard
                        activeTab={activeTab}
                        setActiveTab={handleTabChange}
                        onCanvasRef={handleCanvasRef}
                        onDrawingChange={setHasDrawing}
                        typedName={typedName}
                        setTypedName={setTypedName}
                    />

                    <div className="signature-request-guardian">
                        <SelectTypeAutocomplete
                            title={'Client Guardian*'}
                            name={'guardian'}
                            handleSelect={(e) => setSelectedGuardian(e.target.value)}
                            defaultValue={selectedGuardian}
                            list={request.guardians}
                            renderValue={(i) => renderClientName(i)}
                            noError={true}
                        />
                    </div>

                    <label className="signature-request-confirm">
                        <Checkbox
                            checked={confirmed}
                            onChange={(e) => setConfirmed(e.target.checked)}
                            style={{ padding: 0, color: confirmed ? Colors.ThemeBlue : '#D0D5DD' }}
                        />
                        <span>I confirm that I am the client or authorized parent/guardian and that the signature above is mine.</span>
                    </label>

                    <div className="signature-request-footer">
                        <Button
                            buttonClassName={'signature-request-submit-btn'}
                            buttonDisabled={!canSubmit}
                            buttonLoader={!!signLoader?.length}
                            onClickButton={handleSubmit}
                        >
                            Submit Signature
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="signature-request-page">
            <div className="signature-request-header">
                <button onClick={navigateHome} className='signature-request-header-logo'>
                    <img src={Images.blueBlackLogo} alt="therapylake" />
                </button>
            </div>

            {renderBody()}
        </div>
    );
};
